import { Action, action } from 'easy-peasy'
import { Address, LandingSlide } from '@api/api.types'
import { BusinessAccountType } from '@lib/formSchemas/businessAccountTypeSelection/types'

import { COUNTRIES, Country } from '../lib/countries'

interface OnboardingModel {
  appInitialized?: boolean
  email?: string
  userId?: string
  country: Country
  phone?: string
  accountToken: string
  kycReferenceId: string
  ssnLastFour: string
  firstName: string
  lastName: string
  enteredAddress?: Address
  isMinorAccount: boolean
  businessAccountType?: BusinessAccountType
  hasExistingCard?: boolean
  embedded?: 'native' | 'web' | 'true'
  instanceId?: string
  noCardWanted?: boolean
  isFinishingSignup?: boolean
  isLinkingExistingAccount?: boolean
  nameOnCard: string
  cardLastFour: string
  locationId?: string
  accountNeedsReview: boolean
  accountNeedsAdditionalDocumentation: boolean
  bypassCard?: boolean
  allowExit: boolean
  orgId?: string
  employeeId?: string
  callbackUrl?: string
  splash?: LandingSlide
  landingSlides?: LandingSlide[]
  cardName: string
  instantCardUserToken?: string
  canClaimWallet: boolean
  manualAddressUpdate: boolean
  essentialFlow?: boolean
  birthDate?: Date
  failKycInquiry: boolean
  setAppInitialized: Action<OnboardingModel, boolean>
  setManualAddressUpdate: Action<OnboardingModel, boolean>
  setCanClaimWallet: Action<OnboardingModel, boolean>
  setCardName: Action<OnboardingModel, string>
  setEmbedded: Action<OnboardingModel, 'native' | 'web' | 'true'>
  setInstanceId: Action<OnboardingModel, string>
  setAccountToken: Action<OnboardingModel, string>
  setKycReferenceId: Action<OnboardingModel, string>
  setEmail: Action<OnboardingModel, string>
  setCountry: Action<OnboardingModel, Country>
  setUserId: Action<OnboardingModel, string>
  setPhone: Action<OnboardingModel, string>
  setFirstName: Action<OnboardingModel, string>
  setLastName: Action<OnboardingModel, string>
  setSSNLastFour: Action<OnboardingModel, string>
  setOrgId: Action<OnboardingModel, string>
  setLocationId: Action<OnboardingModel, string>
  setEmployeeId: Action<OnboardingModel, string>
  setBypassCard: Action<OnboardingModel, boolean>
  setEnteredAddress: Action<OnboardingModel, Address>
  setIsMinorAccount: Action<OnboardingModel, boolean>
  setBusinessAccountType: Action<OnboardingModel, BusinessAccountType>
  setCallbackUrl: Action<OnboardingModel, string>
  setHasExistingCard: Action<OnboardingModel, boolean>
  setNoCardWanted: Action<OnboardingModel, boolean>
  setIsFinishingSignup: Action<OnboardingModel, boolean>
  setIsLinkingExistingAccount: Action<OnboardingModel, boolean>
  setNameOnCard: Action<OnboardingModel, string>
  setCardLastFour: Action<OnboardingModel, string>
  setAllowExit: Action<OnboardingModel, boolean>
  setAccountNeedsReview: Action<OnboardingModel, boolean>
  setAccountNeedsAdditionalDocumentation: Action<OnboardingModel, boolean>
  setLandingSlides: Action<OnboardingModel, LandingSlide[]>
  setFailKycInquiry: Action<OnboardingModel, boolean>
  setSplash: Action<OnboardingModel, LandingSlide>
  setInstantCardUserToken: Action<OnboardingModel, string>
  setEssentialFlow: Action<OnboardingModel, boolean>
  setBirthDate: Action<OnboardingModel, Date>
}

const onboardingStore: OnboardingModel = {
  embedded: undefined,
  instanceId: undefined,
  locationId: undefined,
  bypassCard: undefined,
  email: '',
  country: COUNTRIES[0],
  phone: '',
  firstName: '',
  lastName: '',
  manualAddressUpdate: false,
  accountToken: '',
  kycReferenceId: '',
  ssnLastFour: '',
  nameOnCard: '',
  cardLastFour: '',
  userId: undefined,
  enteredAddress: undefined,
  isMinorAccount: false,
  businessAccountType: undefined,
  isFinishingSignup: undefined,
  hasExistingCard: undefined,
  noCardWanted: undefined,
  accountNeedsReview: false,
  accountNeedsAdditionalDocumentation: false,
  orgId: undefined,
  callbackUrl: undefined,
  splash: undefined,
  landingSlides: undefined,
  instantCardUserToken: undefined,
  cardName: 'Branch Card',
  allowExit: false,
  failKycInquiry: false,
  canClaimWallet: false,
  essentialFlow: false,
  birthDate: undefined,
  setEssentialFlow: action((state, val) => {
    state.essentialFlow = val
  }),
  setCanClaimWallet: action((state, val) => {
    state.canClaimWallet = val
  }),
  setEmail: action((state, val) => {
    state.email = val
  }),
  setEmbedded: action((state, val) => {
    state.embedded = val
  }),
  setLocationId: action((state, val) => {
    state.locationId = val
  }),
  setInstanceId: action((state, payload) => {
    state.instanceId = payload
  }),
  setBypassCard: action((state, payload) => {
    state.bypassCard = payload
  }),
  setAllowExit: action((state, payload) => {
    state.allowExit = payload
  }),
  setUserId: action((state, val) => {
    state.userId = val
  }),
  setCallbackUrl: action((state, val) => {
    state.callbackUrl = val
  }),
  setCountry: action((state, val) => {
    state.country = val
  }),
  setPhone: action((state, val) => {
    state.phone = val
  }),
  setOrgId: action((state, val) => {
    state.orgId = val
  }),
  setEmployeeId: action((state, val) => {
    state.employeeId = val
  }),
  setFirstName: action((state, payload) => {
    state.firstName = payload
  }),
  setLastName: action((state, payload) => {
    state.lastName = payload
  }),
  setAccountToken: action((state, payload) => {
    state.accountToken = payload
  }),
  setKycReferenceId: action((state, payload) => {
    state.kycReferenceId = payload
  }),
  setSSNLastFour: action((state, payload) => {
    state.ssnLastFour = payload
  }),
  setEnteredAddress: action((state, val) => {
    state.enteredAddress = val
  }),
  setIsMinorAccount: action((state, val) => {
    state.isMinorAccount = val
  }),
  setBusinessAccountType: action((state, val) => {
    state.businessAccountType = val
  }),
  setCardName: action((state, val) => {
    state.cardName = val
  }),
  setHasExistingCard: action((state, val) => {
    state.hasExistingCard = val
  }),
  setNoCardWanted: action((state, val) => {
    state.noCardWanted = val
  }),
  setIsFinishingSignup: action((state, val) => {
    state.isFinishingSignup = val
  }),
  setIsLinkingExistingAccount: action((state, val) => {
    state.isLinkingExistingAccount = val
  }),
  setNameOnCard: action((state, val) => {
    state.nameOnCard = val
  }),
  setCardLastFour: action((state, val) => {
    state.cardLastFour = val
  }),
  setAccountNeedsReview: action((state, val) => {
    state.accountNeedsReview = val
  }),
  setAccountNeedsAdditionalDocumentation: action((state, val) => {
    state.accountNeedsAdditionalDocumentation = val
  }),
  setLandingSlides: action((state, val) => {
    state.landingSlides = val
  }),
  setSplash: action((state, val) => {
    state.splash = val
  }),
  setInstantCardUserToken: action((state, val) => {
    state.instantCardUserToken = val
  }),
  setAppInitialized: action((state, val) => {
    state.appInitialized = val
  }),
  setManualAddressUpdate: action((state, val) => {
    state.manualAddressUpdate = val
  }),
  setBirthDate: action((state, val) => {
    state.birthDate = val
  }),
  setFailKycInquiry: action((state, val) => {
    state.failKycInquiry = val
  }),
}

export default onboardingStore
